// ============================================================
// ValoracionDialog.tsx
// Permite valorar una cita con puntuación + comentario.
// Uso: <ValoracionDialog idCita="..." open onCloseAll={...} />
// ============================================================

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

import { valorarCita } from '@/components/hooks/useCitas';

interface Props {
  idCita: string;
  open: boolean;
  onCloseAll: () => void;
}

const estrellas = [1, 2, 3, 4, 5];

export default function ValoracionDialog({ idCita, open, onCloseAll }: Props) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(false);

  const enviarValoracion = async () => {
    if (!rating) {
      toast.error('Selecciona una valoración antes de guardar');
      return;
    }

    try {
      setLoading(true);
      const resp = await valorarCita(idCita, rating, comentario);

      if (!resp.ok) {
        toast.error('No se pudo guardar la valoración');
        return;
      }

      toast.success('Valoración registrada correctamente');
      onCloseAll();
    } catch (err) {
      console.error(err);
      toast.error('Error al guardar la valoración');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onCloseAll}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-center text-[#103254] dark:text-blue-400 pr-10">
              Valorar servicio
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={onCloseAll}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-5">
            <div className="flex justify-center gap-3 py-2 flex-wrap">
              {estrellas.map((num) => (
                <motion.button
                  key={num}
                  type="button"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onMouseEnter={() => setHoverRating(num)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(num)}
                  className="text-2xl font-bold leading-none select-none text-yellow-500 dark:text-yellow-400"
                >
                  {(hoverRating || rating) >= num ? '★' : '☆'}
                </motion.button>
              ))}
            </div>

            <Textarea
              placeholder="¿Quieres añadir un comentario? (opcional)"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              rows={4}
              className="rounded-lg text-sm sm:text-base resize-none border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            type="button"
            variant="outline"
            onClick={onCloseAll}
            disabled={loading}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            onClick={enviarValoracion}
            disabled={loading}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY} !bg-yellow-500 hover:!bg-yellow-600`}
          >
            {loading ? 'Guardando…' : 'Enviar valoración'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}